import { jsx as _jsx } from "react/jsx-runtime";
import { createSilentLogger } from '@ibe/logger';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnalyticsContext } from './use-analytics.js';
import { AnalyticsService } from './analytics-service.js';
import { readConsentState } from './consent.js';
export function AnalyticsProvider({ baseUrl, propertyId, logger, children, }) {
    const [service, setService] = useState(null);
    const [config, setConfig] = useState(null);
    const mountedRef = useRef(false);
    const log = useMemo(() => logger ?? createSilentLogger(), [logger]);
    useEffect(() => {
        let cancelled = false;
        async function load() {
            const url = new URL(`${baseUrl.replace(/\/$/u, '')}/v1/public/integrations`);
            if (propertyId !== undefined) {
                url.searchParams.set('propertyId', propertyId);
            }
            try {
                const response = await fetch(url.toString(), {
                    method: 'GET',
                    headers: { Accept: 'application/json' },
                });
                if (cancelled)
                    return;
                if (!response.ok) {
                    log.warn('AnalyticsProvider: failed to load public config', { status: response.status });
                    setService(null);
                    setConfig(null);
                    return;
                }
                const loaded = (await response.json());
                const consent = readConsentState();
                setConfig(loaded);
                setService(new AnalyticsService({ config: loaded, consent, logger: log }));
            }
            catch (error) {
                if (cancelled)
                    return;
                log.warn('AnalyticsProvider: public config load threw', {
                    error: error instanceof Error ? error.message : 'unknown',
                });
                setService(null);
                setConfig(null);
            }
        }
        load().catch(() => {
            // Already logged inside load; this catch satisfies the linter and the
            // fire-and-forget contract that analytics must never throw.
        });
        return () => {
            cancelled = true;
        };
    }, [baseUrl, propertyId, log]);
    useEffect(() => {
        if (config?.csp === undefined)
            return;
        const apiOrigin = new URL(baseUrl).origin;
        const csp = {
            ...config.csp,
            // Signed /v1/attachments/<id> image URLs are minted against the gateway
            // origin, which is not the page origin — allow it for images too.
            'connect-src': `${config.csp['connect-src'] ?? "'self'"} ${apiOrigin}`.trim(),
            'img-src': `${config.csp['img-src'] ?? "'self'"} ${apiOrigin}`.trim(),
        };
        const directives = buildCspDirective(csp);
        if (directives.length === 0)
            return;
        const content = directives.join('; ');
        const existing = document.head.querySelector('meta[http-equiv="Content-Security-Policy"]');
        if (existing !== null) {
            existing.setAttribute('content', content);
        }
        else {
            const meta = document.createElement('meta');
            meta.setAttribute('http-equiv', 'Content-Security-Policy');
            meta.setAttribute('content', content);
            document.head.appendChild(meta);
        }
    }, [config, baseUrl]);
    useEffect(() => {
        if (service === null || mountedRef.current)
            return;
        mountedRef.current = true;
        service.track({
            kind: 'IBE_APP_MOUNTED',
            chainId: config?.tenantId,
            chainName: config?.tenantName,
        });
    }, [service, config?.tenantId, config?.tenantName]);
    return _jsx(AnalyticsContext.Provider, { value: service, children: children });
}
function buildCspDirective(csp) {
    const directives = [];
    const entries = Object.entries(csp);
    for (const [name, value] of entries) {
        if (typeof value === 'string' && value.length > 0) {
            directives.push(`${name} ${value}`);
        }
    }
    return directives;
}
//# sourceMappingURL=analytics-provider.js.map